import React, { useEffect, useState } from 'react';
import RMModal from 'components/modal/RMModal';
import { FmlxBanner, FmlxModal } from 'fmlx-common-ui';

interface Props {
  showModal: boolean;
  setShowModal: (flag: boolean) => void;
  selectedColumns: string[];
  onConfirm: (newOrder: string[]) => void;
}

const ColumnOrderModal = (props: Props) => {
  const { showModal, setShowModal, selectedColumns, onConfirm } = props;
  const [draftOrder, setDraftOrder] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | undefined>(undefined);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (showModal) {
      setDraftOrder([...selectedColumns]);
      setDraggedIndex(undefined);
      setDropTargetIndex(undefined);
    }
  }, [showModal, selectedColumns]);

  const reorder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }
    setDraftOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const onDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    const fromData = e.dataTransfer.getData('text/plain');
    const fromIndex = fromData !== '' ? Number(fromData) : draggedIndex;
    if (fromIndex !== undefined) {
      reorder(fromIndex, toIndex);
    }
    setDraggedIndex(undefined);
    setDropTargetIndex(undefined);
  };

  const onDragEnd = () => {
    setDraggedIndex(undefined);
    setDropTargetIndex(undefined);
  };

  const onConfirmClick = () => {
    onConfirm([...draftOrder]);
    setShowModal(false);
  };

  return (
    <RMModal
      id="snf-table-columnorder-modal"
      showModal={showModal}
      setShowModal={setShowModal}
      heading={'Table Columns'}
      isSaveAvailable={true}
      saveButtonText={'Confirm'}
      onSave={onConfirmClick}
      isCancelOnly={true}
      noButtonText={'Close'}
      onCancel={() => setShowModal(false)}
      size={FmlxModal.Size.LARGE}
      body={
        <div
          id="snf-table-columnorder-pills-container"
          className="flex flex-wrap gap-2 p-2"
        >
          <FmlxBanner
            variant="info"
            title="You can drag and drop the chip to re-arrange the columns."
            hideCloseButton
          />
          {draftOrder.map((columnName, index) => (
            <div
              key={columnName}
              id={`snf-table-columnorder-pill-${columnName}`}
              data-testid={`snf-columnorder-modal-pill-${columnName}`}
              draggable
              onDragStart={(e) => onDragStart(e, index)}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, index)}
              onDragEnter={() => setDropTargetIndex(index)}
              onDragEnd={onDragEnd}
              className={`cursor-grab select-none rounded-full border px-3 py-1 text-sm ${
                draggedIndex === index ? 'opacity-50' : ''
              } ${
                dropTargetIndex === index
                  ? 'border-[#9e9e9e] bg-[#e0e0e0] text-[#313131]'
                  : 'border-[#ccc] bg-[#f5f5f5] text-[#313131]'
              }`}
            >
              {columnName}
            </div>
          ))}
        </div>
      }
    />
  );
};

export default ColumnOrderModal;
